import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { AppLayout } from "@/components/layout/AppLayout";
import { usePlaybookBySlug, usePlaybookNodes, PlaybookNode, getPlaybookSlug } from "@/hooks/usePlaybooks";
import { getIcon } from "@/lib/iconMap";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SharePlaybookDialog } from "@/components/SharePlaybookDialog";
import { 
  ArrowLeft, 
  ArrowRight, 
  CheckCircle2,
  AlertTriangle,
  MessageSquare,
  FileText,
  Loader2
} from "lucide-react";
import { toast } from "sonner";

export default function PlaybookDetail() {
  const { playbookId } = useParams();
  const navigate = useNavigate();
  const [currentNodeId, setCurrentNodeId] = useState("start");
  const [history, setHistory] = useState<string[]>([]);

  const { data: playbook, isLoading: playbookLoading } = usePlaybookBySlug(playbookId);
  const { data: nodes, isLoading: nodesLoading } = usePlaybookNodes(playbook?.id);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth");
      }
    };
    checkAuth();
  }, [navigate]);

  const goBackToPlaybooks = () => navigate("/app/playbooks");

  // Reset when playbook changes
  useEffect(() => {
    setCurrentNodeId("start");
    setHistory([]);
  }, [playbook?.id]);

  const isLoading = playbookLoading || nodesLoading;

  if (isLoading) {
    return (
      <AppLayout>
        <div className="p-4 md:p-8 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AppLayout>
    );
  }

  if (!playbook) {
    return (
      <AppLayout>
          <div className="p-4 md:p-8">
          <Button variant="ghost" onClick={goBackToPlaybooks}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Playbooks
          </Button>
          <div className="mt-8 text-center text-muted-foreground">
            Playbook not found.
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!nodes || nodes.length === 0) {
    return (
      <AppLayout>
          <div className="p-4 md:p-8">
          <Button variant="ghost" onClick={goBackToPlaybooks}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Playbooks
          </Button>
          <div className="mt-8 text-center text-muted-foreground">
            This playbook has no content yet.
          </div>
        </div>
      </AppLayout>
    );
  }

  // Build node map
  const nodeMap: Record<string, PlaybookNode> = {};
  nodes.forEach(node => {
    nodeMap[node.node_id] = node;
  });

  const currentNode = nodeMap[currentNodeId];

  if (!currentNode) {
    return (
      <AppLayout>
        <div className="p-4 md:p-8">
          <Button variant="ghost" onClick={goBackToPlaybooks}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Playbooks
          </Button>
          <div className="mt-8 text-center text-muted-foreground">
            Node not found. The playbook may be incomplete.
          </div>
        </div>
      </AppLayout>
    );
  }

  const handleOptionClick = (option: typeof currentNode.options[0]) => {
    if (option.action) {
      if (option.action === "call_turo" || option.action === "call") {
        window.open("tel:4159654525");
      } else {
        navigator.clipboard.writeText(option.action);
        toast.success("Copied to clipboard");
      }
    }
    if (option.nextNode) {
      setHistory([...history, currentNodeId]);
      setCurrentNodeId(option.nextNode);
    }
  };

  const handleBack = () => {
    if (history.length > 0) {
      const newHistory = [...history];
      const previousNode = newHistory.pop()!;
      setHistory(newHistory);
      setCurrentNodeId(previousNode);
    }
  };

  const getEndIcon = () => {
    switch (currentNode.end_type) {
      case "success":
        return <CheckCircle2 className="h-12 w-12 text-success" />;
      case "warning":
        return <AlertTriangle className="h-12 w-12 text-warning" />;
      case "action":
        return <MessageSquare className="h-12 w-12 text-info" />;
      default:
        return null;
    }
  };

  const Icon = getIcon(playbook.icon_name);


  return (
    <AppLayout>
      <div className="p-4 md:p-8 max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <Button variant="ghost" onClick={goBackToPlaybooks}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Playbooks
            </Button>
            <SharePlaybookDialog 
              playbookTitle={playbook.title}
              playbookSlug={getPlaybookSlug(playbook.title)}
              playbookDescription={playbook.description}
            />
          </div>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon className="h-7 w-7 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{playbook.title}</h1>
              <p className="text-muted-foreground">Interactive decision tree</p>
            </div>
          </div>
        </div>

        {/* Progress */}
        <div className="flex items-center gap-2 mb-6">
          <Badge variant="secondary">
            Step {history.length + 1}
          </Badge>
          {history.length > 0 && (
            <Button variant="ghost" size="sm" onClick={handleBack}>
              <ArrowLeft className="mr-1 h-4 w-4" />
              Back
            </Button>
          )}
        </div>

        {/* Decision Card */}
        <Card className={`${currentNode.is_end ? "border-2" : ""} ${
          currentNode.end_type === "success" ? "border-success" : 
          currentNode.end_type === "warning" ? "border-warning" : 
          currentNode.end_type === "action" ? "border-info" : ""
        }`}>
          <CardHeader className="text-center">
            {currentNode.is_end && (
              <div className="flex justify-center mb-4">
                {getEndIcon()}
              </div>
            )}
            <CardTitle className="text-xl">{currentNode.question}</CardTitle>
            {currentNode.description && (
              <p className="text-muted-foreground mt-2">{currentNode.description}</p>
            )}
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {currentNode.options.map((option, index) => (
                <Button
                  key={index}
                  variant={option.variant === "destructive" ? "destructive" : "outline"}
                  className="w-full justify-between text-left h-auto py-4 px-4"
                  onClick={() => handleOptionClick(option)}
                >
                  <span>{option.label}</span>
                  {option.nextNode && <ArrowRight className="h-4 w-4 flex-shrink-0" />}
                  {option.action && !option.nextNode && (
                    <FileText className="h-4 w-4 flex-shrink-0" />
                  )}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Tips */}
        {currentNode.tip_text && (
          <Card className="mt-6 bg-muted/50">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">
                <strong>Pro tip:</strong> {currentNode.tip_text}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
}
